import axiosInstance from "./axiosConfig";

// API endpoint for stock movements
const STOCK_URL = "/stock/movements";

/**
 * Service for handling stock-related API calls
 */
const stockService = {
  /**
   * Get stock movement history for a specific product
   * @param {string} productId - Product ID
   * @param {Object} params - Query parameters for pagination
   * @returns {Promise<Object>} Stock movement data
   */
  getProductStockMovements: async (productId, params = {}) => {
    try {
      const response = await axiosInstance.get(
        `${STOCK_URL}/product/${productId}`,
        {
          params,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching product stock movements:", error);
      return {
        movements: [],
        pagination: { totalItems: 0, totalPages: 0, page: 1 },
      };
    }
  },

  /**
   * Get all stock movements with pagination and filtering
   * @param {Object} params - Query parameters (page, limit, search, reason)
   * @returns {Promise<Object>} Stock movement data
   */
  getAllStockMovements: async (params = {}) => {
    try {
      const response = await axiosInstance.get(STOCK_URL, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching stock movements:", error);
      return {
        movements: [],
        pagination: { totalItems: 0, totalPages: 0, page: 1 },
      };
    }
  },

  /**
   * Get summary statistics for stock movements
   * @returns {Promise<Object>} Stock movement statistics
   */
  getStockMovementStats: async () => {
    try {
      const response = await axiosInstance.get(`${STOCK_URL}/stats`);
      return response.data;
    } catch (error) {
      console.error("Error fetching stock movement stats:", error);
      return { total: 0, increases: 0, decreases: 0 };
    }
  },

  /**
   * Get possible reasons for stock movements
   * @returns {Promise<Array>} Array of reason options
   */
  getStockMovementReasons: () => {
    return [
      "order_placement",
      "order_cancellation",
      "return_approved",
      "manual_update",
      "initial_stock",
    ];
  },

  /**
   * Create a manual stock movement
   * @param {Object} movementData - Stock movement data including productId, quantity, and reason
   * @returns {Promise<Object>} Created stock movement
   */
  createManualStockMovement: async (movementData) => {
    try {
      const response = await axiosInstance.post(
        `${STOCK_URL}/manual`,
        movementData
      );
      return response.data;
    } catch (error) {
      console.error("Error creating manual stock movement:", error);
      throw error;
    }
  },

  /**
   * Delete a stock movement (soft delete)
   * @param {string} id - Stock movement ID to delete
   * @returns {Promise<Object>} Response with success message
   */
  deleteStockMovement: async (id) => {
    try {
      const response = await axiosInstance.delete(`${STOCK_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting stock movement:", error);
      throw error;
    }
  },
};

export default stockService;
